import { useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { kuniriIconWeight, KuniriIcons } from '@/lib/icons'

interface CoverFieldProps {
  previewUrl: string | null
  existingCoverUrl: string | null
  categoryCoverUrl: string
  categoryColor: string
  fileName: string | null
  error: string | null
  disabled?: boolean
  onSelect: (file: File | null) => void
}

/**
 * Optional cover picker. JPEG/PNG/WebP up to 20 MB; the preview uses a local
 * object URL, and the existing cover is kept when no new file is selected.
 * There is no delete-cover endpoint, so removing a pending file only clears the
 * local preview.
 */
export function CoverField({
  previewUrl,
  existingCoverUrl,
  categoryCoverUrl,
  categoryColor,
  fileName,
  error,
  disabled = false,
  onSelect,
}: CoverFieldProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const src = previewUrl ?? existingCoverUrl ?? categoryCoverUrl

  return (
    <div>
      <div
        className="activity-cover-preview"
        style={{ backgroundColor: categoryColor }}
      >
        {src ? (
          <img
            src={src}
            alt={
              previewUrl
                ? t('activityForm.cover.previewAlt')
                : t('activityForm.cover.fallbackAlt')
            }
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label
          htmlFor={inputId}
          className="btn-secondary min-h-11 cursor-pointer px-4 text-sm"
        >
          <KuniriIcons.imageSquare
            size={18}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {fileName
            ? t('activityForm.cover.change')
            : t('activityForm.cover.choose')}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          disabled={disabled}
          aria-describedby={[error ? errorId : null, hintId]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? true : undefined}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null
            onSelect(file)
            event.target.value = ''
          }}
        />
        {fileName ? (
          <button
            type="button"
            className="btn-ghost min-h-11 px-2 text-sm"
            disabled={disabled}
            onClick={() => {
              if (inputRef.current) inputRef.current.value = ''
              onSelect(null)
            }}
          >
            {t('activityForm.cover.remove')}
          </button>
        ) : null}
      </div>

      {fileName ? (
        <p className="mt-1 text-xs leading-snug text-muted">{fileName}</p>
      ) : null}
      <p id={hintId} className="mt-1 text-xs leading-snug text-muted">
        {t('activityForm.cover.hint')}
      </p>
      {error ? (
        <p
          id={errorId}
          className="mt-1.5 text-sm font-semibold text-danger"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}
